/**
 * Scratch to win: when a card may open, and how long the visitor has to have
 * been on the step before a submit is believable.
 *
 * Pure — no Nuxt, no DOM: every environment fact (a coarse pointer, a hidden
 * document, the referrer, the moment the step appeared) is passed in by
 * `useScratchTrigger` / `useScratchFlow`, which is what lets the rules be
 * tested without a browser.
 */

/** Journeys the card must never interrupt — money in flight, a form mid-way, the owner's own preview. */
export const SUPPRESSED_PATHS = ['/checkout', '/auth/', '/preview/'] as const

export function isSuppressedPath(path: string): boolean {
  return SUPPRESSED_PATHS.some(suppressed => path.includes(suppressed))
}

export const DEFAULT_DELAY_SECONDS = 8
/** The Dashboard accepts 0..120 too; both ends clamp so a bad value cannot hide the card forever. */
export const MAX_DELAY_SECONDS = 120

export function clampDelaySeconds(value: number | null | undefined): number {
  if (typeof value !== 'number' || !Number.isFinite(value))
    return DEFAULT_DELAY_SECONDS
  return Math.min(MAX_DELAY_SECONDS, Math.max(0, Math.round(value)))
}

/**
 * A TIMED card counts from navigation start, so a delay already spent reading
 * is not spent again — but a page armed while the tab is hidden waits the full
 * delay once it is actually looked at.
 */
export function timedRemainingMs(delayMs: number, msSinceNavigation: number, hidden: boolean): number {
  return hidden ? delayMs : Math.max(0, delayMs - msSinceNavigation)
}

/**
 * Hosts whose visitors arrive from a result page. Matched on the whole
 * hostname's last labels, so `googleusercontent.com` (a CDN, not a search) does
 * not count.
 */
export const SEARCH_ENGINE_HOST = /(?:^|\.)(?:google|bing|seznam|duckduckgo|yahoo|ecosia)\.[a-z]{2,}(?:\.[a-z]{2})?$/i

export function isSearchEngineReferrer(referrer: string): boolean {
  try {
    return SEARCH_ENGINE_HOST.test(new URL(referrer).hostname)
  }
  catch {
    return false
  }
}

/**
 * SEO: a TIMED card becomes an exit-intent card on a touch device's search
 * landing page. An interstitial over the content a crawler's own guidelines
 * call the landing experience is the one case where the timer is the wrong
 * trigger; one in-site navigation later it is the right one again.
 */
export function shouldDowngradeTimed(input: { coarsePointer: boolean, landingPageView: boolean, referrer: string }): boolean {
  return input.coarsePointer && input.landingPageView && isSearchEngineReferrer(input.referrer)
}

/**
 * The floor the client keeps for a submit, a little above the server's own
 * brake (1200 ms), so a human who really is that fast is not refused by it.
 */
export const SCRATCH_MIN_ELAPSED_MS = 1300

export function minElapsedWaitMs(stepShownAt: number, now: number): number {
  return Math.max(0, SCRATCH_MIN_ELAPSED_MS - (now - stepShownAt))
}
